/* eslint-disable no-param-reassign */

import { ProductTypes } from '../../shared/constants';
import {
  BundleReportItem,
  ProductReport,
  ReportOverviewProps,
  ReportTotalProps,
  TotalProductProps,
} from '../../shared/models/ReportModel';

const IgnoreStatuses = ['Pending', 'Terminated'];

export function getEnrolledCountForDigitalProducts(product: ProductReport) {
  if (!product.bundle) return product.statusCounts?.New ?? 0;

  return product.quantityRedeemed;
}

export function getEnrolledNewCountForMembership(product: ProductReport) {
  if (!product.bundle) return product.statusCounts?.New ?? 0;

  let totalNewStatus = 0;
  product.bundleProductItems
    ?.filter(
      (item) =>
        !item.type.toLowerCase().includes('chapter') &&
        !item.name.toLowerCase().includes('fee'),
    )
    ?.forEach((item) => {
      totalNewStatus +=
        item.statusCounts && item.statusCounts.New ? item.statusCounts.New : 0;
    });

  return totalNewStatus;
}

export function getEnrolledRenewCountForMembership(product: ProductReport) {
  if (!product.bundle) return product.statusCounts?.Renew ?? 0;

  let totalRenewStatus = 0;
  product.bundleProductItems
    ?.filter((item) => item.type.toLocaleLowerCase() !== 'chapter')
    ?.forEach((item) => {
      totalRenewStatus +=
        item.statusCounts && item.statusCounts.Renew
          ? item.statusCounts.Renew
          : 0;
    });

  return totalRenewStatus;
}

function sum(
  products: ProductReport[],
  sumSelector: (product: ProductReport) => number,
) {
  let total = 0;
  products.forEach((p) => {
    total += sumSelector(p);
  });
  return total;
}

function mergeItemsOfType(
  items: BundleReportItem[],
  productType: string,
): BundleReportItem[] {
  const matchingItems = items.filter((item) => item.type === productType);

  // Nothing to merge when there is at most one matching item
  if (matchingItems.length <= 1) return items;

  const mergedStatusCounts: { [key: string]: number } = {};
  matchingItems.forEach((item) => {
    Object.keys(item.statusCounts ?? {}).forEach((key) => {
      mergedStatusCounts[key] =
        (mergedStatusCounts[key] ?? 0) + (item.statusCounts[key] ?? 0);
    });
  });

  const mergedItem: BundleReportItem = {
    sku: matchingItems[0].sku,
    type: matchingItems[0].type,
    name: matchingItems[0].name,
    statusCounts: mergedStatusCounts,
    bundleItemSkus: matchingItems.map((e) => e.sku),
  };

  // Keep the original ordering, replacing the first matching occurrence with
  // the merged item and dropping any subsequent duplicates of the same type.
  const result: BundleReportItem[] = [];
  let mergedInserted = false;
  items.forEach((item) => {
    if (item.type === productType) {
      if (!mergedInserted) {
        result.push(mergedItem);
        mergedInserted = true;
      }
      return;
    }
    result.push(item);
  });

  return result;
}

export function getBundleItems(product: ProductReport): BundleReportItem[] {
  let items: BundleReportItem[] = product.bundleProductItems ?? [];
  if (product.type === ProductTypes.membership) {
    items = items.filter(
      (e) =>
        !e.type.toLowerCase().includes('chapter') &&
        !e.name.toLowerCase().includes('fee'),
    );
    items = mergeItemsOfType(items, ProductTypes.membership);
  }

  if (product.type === ProductTypes.onlineCourse) {
    items = mergeItemsOfType(items, ProductTypes.onlineCourse);
  }

  return items;
}

export function getTypeLabel(type: string) {
  return type === 'eLearning' ? 'Course' : type;
}

export function getStatusCounts(product: Pick<ProductReport, 'statusCounts'>) {
  const statusCounts: { [key: string]: number } = {};
  Object.keys(product.statusCounts ?? {}).forEach((key) => {
    if (IgnoreStatuses.includes(key)) return;
    statusCounts[key] = product.statusCounts[key];
  });

  return statusCounts;
}

function updateCommonTotals(
  productTotal: TotalProductProps,
  products: ProductReport[],
) {
  productTotal.totalSpend = sum(products, (e) => e.totalSpend);
  productTotal.quantityTotal = sum(products, (e) => e.quantityTotal);
  productTotal.quantityRemaining = sum(
    products.filter((p) => !p.retired),
    (e) => e.quantityRemaining,
  );
  productTotal.quantityNotRedeemed = sum(
    products.filter((p) => !p.retired),
    (e) => e.quantityNotRedeemed,
  );
  productTotal.quantityRedeemed = sum(products, (e) => e.quantityRedeemed);
}

function updateStatusTotal(
  statusKey: string,
  productTotal: TotalProductProps,
  products: ProductReport[],
  sumSelector: (product: ProductReport) => number,
) {
  if (!productTotal.statusCounts) productTotal.statusCounts = {};

  productTotal.statusCounts[statusKey] = sum(products, sumSelector);
}

export function getReportTotals(
  overview: ReportOverviewProps,
): ReportTotalProps {
  const total: ReportTotalProps = {
    certifications: {},
    courses: {},
    digitalProducts: {},
    memberships: {},
  };

  updateCommonTotals(total.certifications, overview.certifications);
  updateCommonTotals(total.memberships, overview.memberships);
  updateCommonTotals(total.courses, overview.courses);
  updateCommonTotals(total.digitalProducts, overview.digitalProducts);

  // memberships
  updateStatusTotal(
    'New',
    total.memberships,
    overview.memberships,
    getEnrolledNewCountForMembership,
  );
  updateStatusTotal(
    'Renew',
    total.memberships,
    overview.memberships,
    getEnrolledRenewCountForMembership,
  );

  // certifications
  updateStatusTotal(
    'Certified',
    total.certifications,
    overview.certifications,
    (p) => p.statusCounts.Certified ?? 0,
  );

  // online course
  updateStatusTotal(
    'Completed',
    total.courses,
    overview.courses,
    (p) => p.statusCounts.Completed ?? 0,
  );

  // digital products
  updateStatusTotal(
    'New',
    total.digitalProducts,
    overview.digitalProducts,
    getEnrolledCountForDigitalProducts,
  );
  updateStatusTotal(
    'Renew',
    total.digitalProducts,
    overview.digitalProducts,
    (p) => p.statusCounts.Renew ?? 0,
  );

  total.totalSpend =
    (total.certifications.totalSpend ?? 0) +
    (total.courses.totalSpend ?? 0) +
    (total.digitalProducts.totalSpend ?? 0) +
    (total.memberships.totalSpend ?? 0);

  return total;
}
